import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CircleStackIcon,
  ArrowDownTrayIcon,
  ShieldExclamationIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { PaginatedResponse, Dataset, Export, SuppressionList } from '../types'

export default function DashboardPage() {
  // Fetch recent datasets
  const { data: datasetsData } = useQuery({
    queryKey: ['datasets', 1],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Dataset>>('/datasets', {
        params: { page_size: 5 },
      })
      return res.data
    },
  })
  
  // Fetch recent exports
  const { data: exportsData } = useQuery({
    queryKey: ['exports', 1],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Export>>('/exports', {
        params: { page_size: 5 },
      })
      return res.data
    },
  })
  
  // Fetch suppression lists
  const { data: suppressionsData } = useQuery({
    queryKey: ['suppressions', 1],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<SuppressionList>>('/suppression-lists', {
        params: { page_size: 5 },
      })
      return res.data
    },
  })
  
  const stats = [
    {
      name: 'Total Datasets',
      value: datasetsData?.total || 0,
      icon: CircleStackIcon,
      href: '/datasets',
    },
    {
      name: 'Total Exports',
      value: exportsData?.total || 0,
      icon: ArrowDownTrayIcon,
      href: '/exports',
    },
    {
      name: 'Suppression Lists',
      value: suppressionsData?.total || 0,
      icon: ShieldExclamationIcon,
      href: '/suppressions',
    },
  ]
  
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Welcome to your data platform. Upload, filter, and export your data.
        </p>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="card p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-8 w-8 text-primary-600" aria-hidden="true" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
      
      {/* Quick actions */}
      <div className="card p-6 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link to="/upload" className="btn-primary">
            <CloudArrowUpIcon className="h-5 w-5 mr-2" />
            Upload CSV
          </Link>
          <Link to="/datasets" className="btn-secondary">
            <CircleStackIcon className="h-5 w-5 mr-2" />
            Browse Datasets
          </Link>
          <Link to="/exports" className="btn-secondary">
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            View Exports
          </Link>
        </div>
      </div>
      
      {/* Recent datasets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Datasets</h2>
            <Link to="/datasets" className="text-sm text-primary-600 hover:text-primary-700">
              View all →
            </Link>
          </div>
          {datasetsData?.items.length ? (
            <ul className="divide-y divide-gray-200">
              {datasetsData.items.map((dataset) => (
                <li key={dataset.id}>
                  <Link
                    to={`/datasets/${dataset.id}`}
                    className="block py-3 hover:bg-gray-50 -mx-6 px-6"
                  >
                    <div className="flex justify-between">
                      <p className="text-sm font-medium text-gray-900">{dataset.name}</p>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        dataset.status === 'ready' 
                          ? 'bg-green-100 text-green-700'
                          : dataset.status === 'processing'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {dataset.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {dataset.row_count.toLocaleString()} rows • {dataset.column_count} columns
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 py-4">
              No datasets yet. <Link to="/upload" className="text-primary-600">Upload your first CSV</Link>
            </p>
          )}
        </div>
        
        {/* Recent exports */}
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Exports</h2>
            <Link to="/exports" className="text-sm text-primary-600 hover:text-primary-700">
              View all →
            </Link>
          </div>
          {exportsData?.items.length ? (
            <ul className="divide-y divide-gray-200">
              {exportsData.items.map((exp) => (
                <li key={exp.id} className="py-3">
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-gray-900">{exp.name}</p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      exp.status === 'completed' 
                        ? 'bg-green-100 text-green-700'
                        : exp.status === 'processing'
                        ? 'bg-yellow-100 text-yellow-700'
                        : exp.status === 'pending'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {exp.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {exp.exported_count?.toLocaleString() || '—'} rows exported
                    {exp.campaign_name && ` • ${exp.campaign_name}`}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 py-4">
              No exports yet.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
